/**
 * Add a given number of hours to a given date.
 *
 * @param {Date} date Date to add hours to.
 * @param {number} hours Number of hours to add.
 */
export const addHoursToDate = (date, hours) => {
    date.setHours(date.getHours() + hours);
};
/**
 * Add a given number of days to a given date.
 *
 * @param {Date} date Date to add days to.
 * @param {number} days Number of days to add.
 */
export const addDaysToDate = (date, days) => {
    date.setDate(date.getDate() + days);
};
/**
 * Add a given number of months to a given date.
 *
 * @param {Date} date Date to add months to.
 * @param {number} months Number of months to add.
 */
export const addMonthsToDate = (date, months) => {
    date.setMonth(date.getMonth() + months);
};
/**
 * Subtract a given number of days from a given date.
 *
 * @param {Date} date Date to substract days from.
 * @param {number} days Number of days to subtract.
 */
export const subtractDaysFromDate = (date, days) => {
    date.setDate(date.getDate() - days);
};
/**
 * Subtract a given number of months from a given date.
 *
 * @param {Date} date Date to substract months from.
 * @param {number} months Number of months to subtract.
 */
export const subtractMonthsFromDate = (date, months) => {
    date.setMonth(date.getMonth() - months);
};
/**
 * Preload images starting from an array of sources (images URLs) to avoid images flashing.
 *
 * @param {string[]} sources Array of images sources.
 *
 * @return {Promise<void[]>} A promise resolved when all images have been preloaded.
 */
export const cacheImages = async (sources) => {
    const promises = sources.map((src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = src;
            img.onload = () => resolve();
            img.onerror = () => reject();
        });
    });
    return await Promise.all(promises);
};
/**
 * Calculate the week in which a given day is contained and return all the dates of the
 * calculated week (starting from monday).
 *
 * @param {Date} date Day to calculate the week.
 *
 * @return {Date[]} Array that contains all dates of the calculated week (starting from monday).
 */
export const getDaysInWeek = (date) => {
    var curr = new Date(date);
    var currDay = curr.getDay() === 0 ? 7 : curr.getDay();
    var first = curr.getDate() - currDay + 1;
    var firstWeekDay = new Date(curr.setDate(first));
    let currentWeek = [firstWeekDay];
    for (let i = 1; i < 7; i++) {
        let datetToPush = new Date(firstWeekDay);
        datetToPush.setDate(datetToPush.getDate() + i);
        currentWeek.push(datetToPush);
    }
    return currentWeek;
};
/**
 * Calculate the month in which a given day is contained and return all the dates of the
 * calculated month.
 *
 * @param {Date} date Day to calculate the month.
 *
 * @return {Date[]} Array that contains all dates of the calculated month.
 */
export const getDaysInMonth = (date) => {
    let curr = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 1));
    let days = [];
    while (curr.getMonth() === date.getMonth()) {
        days.push(new Date(curr));
        curr.setDate(curr.getDate() + 1);
    }
    if (days[0].getDay() === 0) {
        days.unshift(...Array(6).fill(""));
    }
    else if (days[0].getDay() !== 1) {
        days.unshift(...Array(days[0].getDay() - 1).fill(""));
    }
    return days;
};
/**
 * Return a given number as string with separators
 * (for example 1362958 becomes 1.362.958).
 *
 * @param {number} n Number to add separators to.
 * @param {"." | ","} [separator="."] Separator to use.
 *
 * @return {string} Number with separators as string.
 */
export const numberWithSeparators = (n, separator = ".") => {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
};
/**
 * Parse a date into a readable string.
 *
 * @param {Date} date Date to parse.
 * @param {"en" | "it"} [language="en"] Language to use for the month.
 * @param {boolean} [printTime=true] If add hours and minutes to the parsed date or not.
 *
 * @return {string} Parsed date.
 */
export const parseDate = (date, language = "en", printTime = true) => {
    let months = language === "en"
        ? [
            "JAN",
            "FEB",
            "MAR",
            "APR",
            "MAY",
            "JUN",
            "JUL",
            "AUG",
            "SEP",
            "OCT",
            "NOV",
            "DEC",
        ]
        : [
            "GEN",
            "FEB",
            "MAR",
            "APR",
            "MAG",
            "GIU",
            "LUG",
            "AGO",
            "SET",
            "OTT",
            "NOV",
            "DIC",
        ];
    if (printTime) {
        return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()} ${date.getHours().toString().length === 1
            ? "0" + date.getHours()
            : date.getHours()}:${date.getMinutes().toString().length === 1
            ? "0" + date.getMinutes()
            : date.getMinutes()}`;
    }
    else {
        return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    }
};
/**
 * Create a deep copy of an object or an array.
 *
 * @param {object | any[]} input Object or array to create a deep copy of.
 *
 * @return {object | any[]} Deep copy of the given object or array.
 */
export const deepCopy = (input) => {
    return JSON.parse(JSON.stringify(input));
};
/**
 * Capitalize the first character of a given string.
 *
 * @param {string} text String to capitalize the first character of.
 *
 * @return {string} Given string with first character capitalized.
 */
export const capitalizeFirstCharacter = (text) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
};
/**
 * Calculate the time elapsed since a given date and return it in a readable form.
 *
 * @param {Date} date Date to calculate the time elapsed from.
 *
 * @throws {Error} Will throw an "Invalid Date" error if the given date is greater than new Date().
 * @return {string} Time elapsed in a readable form.
 */
export const calculateTimeElapsed = (date) => {
    let today = new Date();
    let milliseconds = today.getTime() - date.getTime();
    if (milliseconds < 0) {
        throw new Error("Invalid Date");
    }
    if (milliseconds < 1000) {
        return "just now";
    }
    let seconds = Math.round(milliseconds / 1000);
    if (seconds < 120) {
        return "1 minute ago";
    }
    let minutes = Math.round(seconds / 60);
    if (minutes < 60) {
        return minutes + " minutes ago";
    }
    if (minutes >= 60 && minutes < 120) {
        return "1 hour ago";
    }
    let hours = Math.round(minutes / 60);
    if (hours < 24) {
        return hours + " hours ago";
    }
    if (hours >= 24 && hours < 48) {
        return "1 day ago";
    }
    let days = Math.round(hours / 24);
    if (days < 7) {
        return days + " days ago";
    }
    if (days >= 7 && days < 14) {
        return "1 week ago";
    }
    let weeks = Math.round(days / 7);
    if (weeks < 4) {
        return weeks + " weeks ago";
    }
    if (weeks >= 4 && weeks < 8) {
        return "1 month ago";
    }
    let months = Math.round(weeks / 4);
    if (months < 12) {
        return months + " months ago";
    }
    if (months >= 12 && months < 24) {
        return "1 year ago";
    }
    let years = Math.round(months / 12);
    return years + " years ago";
};
/**
 * Generate a random string based on the set of characters specified.
 *
 * @param {number} length Length of the generated string.
 * @param {"alphanumeric"
 *        | "alphanumericUppercaseOnly"
 *        | "alphanumericLowercaseOnly"
 *        | "numeric"
 *        | "alphabetic"
 *        | "alphabeticUppercaseOnly"
 *        | "alphabeticLowercaseOnly"} [set="alphanumeric"] Set of characters to use.
 *
 * @return {string} Generated random string.
 */
export const generateRandomString = (length, set = "alphanumeric") => {
    let result = "";
    const characters = set === "alphanumeric"
        ? "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
        : set === "alphabetic"
            ? "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"
            : set === "alphabeticLowercaseOnly"
                ? "abcdefghijklmnopqrstuvwxyz"
                : set === "alphabeticUppercaseOnly"
                    ? "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
                    : set === "alphanumericLowercaseOnly"
                        ? "abcdefghijklmnopqrstuvwxyz0123456789"
                        : set === "alphanumericUppercaseOnly"
                            ? "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
                            : "0123456789";
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
};
/**
 * Check if the given string is a valid URL or not.
 *
 * @param {string} stringToTest String to check.
 *
 * @return {boolean} True if the given string is a valid URL, otherwise false.
 */
export const isValidURL = (stringToTest) => {
    const pattern = new RegExp("^(http://www.|https://www.|http://|https://)?[a-z0-9]+([-.]{1}[a-z0-9]+)*.[a-z]{2,5}(:[0-9]{1,5})?(/.*)?$");
    return pattern.test(stringToTest);
};
/**
 * Check if the given URL is a valid YouTube URL or not.
 *
 * @param {string} url URL to check.
 *
 * @return {boolean} True if the given URL is a valid YouTube URL, otherwise false.
 */
export const isValidYouTubeURL = (url) => {
    return /www.youtube.com\/[A-z0-9]+/.test(url);
};
/**
 * Copy the given string to the user clipboard.
 *
 * @param {string} text String to copy.
 */
export const copyStringToClipboard = (text) => {
    navigator.clipboard.writeText(text);
};
/**
 * Return a given enum as an array of strings.
 * Based on this stackoverflow question: https://stackoverflow.com/questions/41308123/map-typescript-enum.
 *
 * @param {any} enumToMap Enum to get the values from.
 *
 * @return {(string | number | symbol)[]} Array that contains all the value of the given enum.
 */
export const enumAsArray = (enumToMap) => {
    return Object.keys(enumToMap);
};
/**
 * Check if a given string date is in the format yyyy-mm-dd.
 *
 * @param {string} stringDate String date to check.
 *
 * @return {boolean} True if the given string date is in the format yyyy-mm-dd, otherwise false.
 */
export const isValidStringDate = (stringDate) => {
    var regEx = /^\d{4}-\d{2}-\d{2}$/;
    if (!stringDate.match(regEx))
        return false; // Invalid format
    var d = new Date(stringDate);
    var dNum = d.getTime();
    if (!dNum && dNum !== 0)
        return false; // NaN value, Invalid date
    return d.toISOString().slice(0, 10) === stringDate;
};
/**
 * Strip all HTML tags from a given string.
 *
 * @param {string} html String to strip HTML tags from.
 *
 * @return {string} Stripped string.
 */
export const stripHtml = (html) => {
    let tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
};
/**
 * Convert a given blob to a base64 string.
 *
 * @param {Blob} blob Blob to convert.
 *
 * @return {Promise<string | ArrayBuffer | null>} Promise to wait for the conversion.
 */
export const blobToBase64 = (blob) => {
    return new Promise((resolve, _) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
    });
};
/**
 * Get the number of days between two given dates.
 *
 * @param {Date} firstDate First date of the interval.
 * @param {Date} secondDate Second date of the interval.
 *
 * @return {number} Number of days between given dates.
 */
export const getDaysNumberBetweenDates = (firstDate, secondDate) => {
    let difference = firstDate.getTime() - secondDate.getTime();
    let totalDays = Math.ceil(difference / (1000 * 3600 * 24));
    return totalDays;
};
